/**
 * Change a machine's status (FS-015).
 *
 * The badge used to be a label. The lifecycle engine, the transitions endpoint
 * and the audit trail all existed; nothing on the frontend called any of them,
 * so a status could only be changed by sending `{status: "..."}` to the asset
 * update endpoint — which, because that path passed `force=True`, skipped both
 * the lifecycle's own rules and the "this status requires a reason" setting.
 *
 * Two lists, never one. `allowed` is what this machine's lifecycle permits from
 * where it is; `override` is everything else. They are different acts — one
 * follows the process, the other steps outside it — and the second always needs
 * a reason, so offering them as one list would be one button for two things.
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ChevronDown } from "lucide-react";
import { apiClient } from "@/api/client";
import { toast } from "@/stores/toastStore";
import AssetStatusBadge from "./AssetStatusBadge";
import Button from "@/components/ui/Button";

interface TransitionOption {
  name: string;
  color: string | null;
  phase: string | null;
  requires_reason: boolean;
}

interface TransitionsResponse {
  current: {
    name: string;
    phase: string | null;
    is_terminal: boolean;
    unknown?: boolean;
  };
  allowed: TransitionOption[];
  override: TransitionOption[];
}

interface Props {
  assetId: string;
  status: string;
  /** Hide the control for a user who may look but not change. */
  canEdit?: boolean;
}

function StatusPicker({ assetId, status, canEdit = true }: Props) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState<TransitionOption | null>(null);
  const [isOverride, setIsOverride] = useState(false);
  const [reason, setReason] = useState("");

  const { data } = useQuery({
    queryKey: ["asset-transitions", assetId, status],
    queryFn: async () => {
      const resp = await apiClient.get<TransitionsResponse>(
        `/api/v1/assets/${assetId}/transitions`,
      );
      return resp.data;
    },
    enabled: open,
  });

  const change = useMutation({
    mutationFn: async (input: {
      status: string;
      reason: string;
      override: boolean;
    }) => {
      const resp = await apiClient.put(`/api/v1/assets/${assetId}`, {
        status: input.status,
        status_reason: input.reason || null,
        status_override: input.override,
      });
      return resp.data;
    },
    onSuccess: (_result, input) => {
      qc.invalidateQueries({ queryKey: ["asset", assetId] });
      qc.invalidateQueries({ queryKey: ["assets"] });
      qc.invalidateQueries({ queryKey: ["asset-transitions", assetId] });
      toast.success(`Moved to ${input.status}`);
      reset();
    },
    onError: (error: unknown) => {
      // The server's sentence says what is wrong and what to do — a status
      // that needs a reason, a move the lifecycle does not allow. Replacing it
      // with "Update failed" is how the reason requirement went unnoticed for
      // as long as it did.
      const detail =
        (error as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? "That status change could not be made.";
      toast.error(detail);
    },
  });

  const reset = () => {
    setOpen(false);
    setTarget(null);
    setIsOverride(false);
    setReason("");
  };

  // A reason is required when the target says so, and always when overriding.
  const reasonRequired = isOverride || Boolean(target?.requires_reason);
  const canSubmit = !reasonRequired || reason.trim().length > 0;

  if (!canEdit) return <AssetStatusBadge status={status} />;

  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 rounded-[var(--radius-md)] px-0.5 transition-colors hover:bg-[var(--surface-sunken)]"
        aria-haspopup="menu"
        aria-expanded={open}
        title="Change status"
      >
        <AssetStatusBadge status={status} />
        <ChevronDown className="h-3.5 w-3.5 text-[var(--text-tertiary)]" />
      </button>

      {open && (
        <div
          className="absolute left-0 z-40 mt-2 w-80 rounded-[var(--radius-lg)] border border-[var(--border-default)] bg-[var(--surface-raised)] shadow-[var(--shadow-xl)]"
          role="menu"
        >
          {data?.current.unknown && (
            <div className="flex gap-2 border-b border-[var(--border-subtle)] bg-[var(--fill-danger)] px-3 py-2">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[var(--text-danger)]" />
              <p className="text-xs text-[var(--text-secondary)]">
                <span className="font-semibold">{data.current.name}</span> is
                not a status in your lifecycle, so nothing can move this machine
                on from it without an override.
              </p>
            </div>
          )}

          {target === null ? (
            <div className="max-h-80 overflow-y-auto py-1.5">
              <p className="px-3 pb-1 pt-1 text-[11px] font-semibold uppercase tracking-wide text-[var(--text-tertiary)]">
                Next
              </p>
              {data?.allowed.length === 0 && (
                <p className="px-3 pb-2 text-xs text-[var(--text-secondary)]">
                  {data.current.is_terminal
                    ? "This is a final status."
                    : "Nothing is configured as a next step."}
                </p>
              )}
              {data?.allowed.map((option) => (
                <button
                  key={option.name}
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setTarget(option);
                    setIsOverride(false);
                  }}
                  className="flex w-full items-center justify-between px-3 py-1.5 text-left text-sm text-[var(--text-primary)] hover:bg-[var(--surface-sunken)]"
                >
                  <span className="flex items-center gap-2">
                    <span
                      className="h-2 w-2 rounded-full"
                      style={{
                        background: option.color ?? "var(--text-tertiary)",
                      }}
                    />
                    {option.name}
                  </span>
                  {option.requires_reason && (
                    <span className="text-[11px] text-[var(--text-tertiary)]">
                      needs a reason
                    </span>
                  )}
                </button>
              ))}

              {data && data.override.length > 0 && (
                <>
                  <p className="mt-1 border-t border-[var(--border-subtle)] px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-[var(--text-tertiary)]">
                    Override — outside the lifecycle
                  </p>
                  {data.override.map((option) => (
                    <button
                      key={option.name}
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setTarget(option);
                        setIsOverride(true);
                      }}
                      className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)]"
                    >
                      <span
                        className="h-2 w-2 rounded-full"
                        style={{
                          background: option.color ?? "var(--text-tertiary)",
                        }}
                      />
                      {option.name}
                    </button>
                  ))}
                </>
              )}
            </div>
          ) : (
            <div className="space-y-2.5 p-3">
              <p className="text-sm text-[var(--text-primary)]">
                {status} <span className="text-[var(--text-tertiary)]">→</span>{" "}
                <span className="font-semibold">{target.name}</span>
              </p>
              {isOverride && (
                <p className="rounded-[var(--radius-md)] bg-[var(--fill-danger)] px-2.5 py-1.5 text-xs text-[var(--text-secondary)]">
                  Your lifecycle does not allow this move. It will be recorded
                  as an override, with your reason, against this machine.
                </p>
              )}
              <label className="block">
                <span className="text-xs font-medium text-[var(--text-secondary)]">
                  Reason{reasonRequired ? "" : " (optional)"}
                </span>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={2}
                  maxLength={500}
                  autoFocus
                  className="mt-1 w-full rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2 py-1.5 text-sm text-[var(--text-primary)]"
                  placeholder={
                    isOverride
                      ? "Why this machine is skipping the usual route"
                      : "Recorded against the machine's history"
                  }
                />
              </label>
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="ghost" onClick={reset}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  disabled={!canSubmit}
                  isLoading={change.isPending}
                  onClick={() =>
                    change.mutate({
                      status: target.name,
                      reason: reason.trim(),
                      override: isOverride,
                    })
                  }
                >
                  Change status
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default StatusPicker;
